import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { apiAbortController, BaseTransaction, Response } from '@ui/src';
import {
  Channel,
  CofProps,
} from '@ui/src/components/transactions-sidepanels/types';
import api from '../../../api/api';
import { Organization } from './types';
import {
  ListOfWarnings,
  MonitoringRulesWarningReport,
  TransactionMonitoringRule,
  WarningsNumberOfAlerts,
} from '../types';

export const warningsReportApi = api.injectEndpoints({
  endpoints: (build) => ({
    getWarningReportsDictionaries: build.query<any, void>({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const dictionaries = await Promise.all([
          fetchWithBQ('/v1/transactions-types'),
          fetchWithBQ('/v1/payment-categories?all=true'),
          fetchWithBQ('/v1/risk-score-ranges?all=true'),
          fetchWithBQ('/v1/blacklists?all=true&active=true'),
        ]);

        return dictionaries
          ? {
              data: {
                transactionTypes: dictionaries[0].data,
                paymentTypes: dictionaries[1].data,
                riskScoreRanges: dictionaries[2].data,
                blacklists: dictionaries[3].data,
              },
            }
          : { error: { status: 'FETCH_ERROR' } as FetchBaseQueryError };
      },
    }),
    getOrganizations: build.query<Response<Organization>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations?take=20${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMonitoringRules: build.query<
      Response<TransactionMonitoringRule>,
      string
    >({
      query: (query) => ({
        url: `/v1/monitorings-rules?take=10${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMonitoringRulesByIds: build.query<
      Response<TransactionMonitoringRule>,
      string
    >({
      query: (query) => ({
        url: `/v1/monitorings-rules?all=true${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOrganizationsByIds: build.query<Response<Organization>, string>({
      query: (query) => ({
        url: `/v1/clients-organizations?all=true${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getMonitoringRulesWarningsReport: build.query<
      { data: MonitoringRulesWarningReport },
      string
    >({
      query: (query) => ({
        url: `/v1/warnings/monitorings-rules/report${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWarningsReport: build.query<{ data: WarningsNumberOfAlerts }, string>({
      query: (query) => ({
        url: `/v1/warnings/report${query}${
          query ? '&' : '?'
        }includes=report-details`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOrganizationsWarningsReport: build.query<
      { data: WarningsNumberOfAlerts },
      string
    >({
      query: (query) => ({
        url: `/v1/warnings/report${query}${
          query ? '&' : '?'
        }includes=organizations&includes=report-details`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOrganizationWarningReportById: build.query<
      { data: WarningsNumberOfAlerts },
      { id: string; query?: string }
    >({
      query: (data) => ({
        url: `/v1/warnings/report?includes=report-details&currentOrganizationId=${data.id}${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getOrganizationMonitoringRulesWarningsReport: build.query<
      { data: MonitoringRulesWarningReport },
      { query: string; id: string }
    >({
      query: (data) => ({
        url: `/v1/warnings/monitorings-rules/report?currentOrganizationId=${data.id}${data.query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getListOfWarnings: build.query<Response<ListOfWarnings>, string>({
      query: (query) => ({
        url: `/v1/warnings${query}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWarningById: build.query<{ data: ListOfWarnings }, string>({
      query: (id) => ({
        url: `/v1/warnings/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWarningDetailsTransaction: build.query<
      { data: BaseTransaction },
      string
    >({
      query: (id) => ({
        url: `/v1/transactions/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWarningDetailsEmployee: build.query<
      {
        data: { id: string; profile: { firstName: string; lastName: string } };
      },
      string
    >({
      query: (id) => ({
        url: `/v1/employees/${id}`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWarningDetailsClientsOfficias: build.query<Response<CofProps>, string>({
      query: (id) => ({
        url: `/v1/clients-officials?organizationId=${id}&status=APPROVED&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    getWarningDetailsChats: build.query<Response<Channel>, string>({
      query: (id) => ({
        url: `/v1/channels?transactionId=${id}&all=true`,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportWarningReport: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/warnings/report/excel',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
    exportListOfWarningReport: build.mutation<
      any,
      Record<string, string[] | string | number>
    >({
      query: (body) => ({
        url: '/v1/warnings/csv',
        method: 'POST',
        body,
        signal: apiAbortController.getSignalToken(),
      }),
    }),
  }),
});

export const {
  useLazyGetWarningReportsDictionariesQuery,
  useLazyGetOrganizationsQuery,
  useLazyGetWarningsReportQuery,
  useLazyGetListOfWarningsQuery,
  useLazyGetWarningDetailsTransactionQuery,
  useLazyGetWarningDetailsEmployeeQuery,
  useLazyGetWarningDetailsClientsOfficiasQuery,
  useLazyGetWarningDetailsChatsQuery,
  useLazyGetOrganizationsWarningsReportQuery,
  useLazyGetOrganizationsByIdsQuery,
  useLazyGetWarningByIdQuery,
  useLazyGetOrganizationWarningReportByIdQuery,
  useExportWarningReportMutation,
  useLazyGetMonitoringRulesWarningsReportQuery,
  useLazyGetOrganizationMonitoringRulesWarningsReportQuery,
  useExportListOfWarningReportMutation,
  useLazyGetMonitoringRulesByIdsQuery,
  useLazyGetMonitoringRulesQuery,
} = warningsReportApi;

export const {
  endpoints: {
    getWarningReportsDictionaries,
    getWarningsReport,
    getListOfWarnings,
    getWarningDetailsTransaction,
    getWarningDetailsEmployee,
    getWarningDetailsClientsOfficias,
    getWarningDetailsChats,
    getOrganizationsWarningsReport,
    getOrganizationsByIds,
    getWarningById,
    getOrganizationWarningReportById,
    exportWarningReport,
    getMonitoringRulesWarningsReport,
    getOrganizationMonitoringRulesWarningsReport,
    exportListOfWarningReport,
    getMonitoringRules,
    getMonitoringRulesByIds,
  },
} = warningsReportApi;
